<template>
  <v-card class="league-share-dialog" max-width="600px">
    <v-icon class="icon ml-auto d-block pt-2 mr-4" icon @click="$emit('close')">fa-regular fa-times</v-icon>
    <v-card-title class="d-flex mt-4">
      <h2 class="mb-4">{{ $t('broker.leagues.shareTitle') || 'Share League Ranking' }}</h2>
    </v-card-title>
    <v-card-text class="scrollable-content">
      <div>
        <p class="mb-6">{{ $t('broker.leagues.shareDescription') || 'Share this league ranking with your community or on social media.' }}</p>

        <!-- League Ranking Link -->
        <div class="link-section mb-5">
          <h4 class="section-title mb-3">{{ $t('broker.leagues.leagueRankingLink') || 'League Ranking Link' }}</h4>
          <p class="section-description mb-3">{{ $t('broker.leagues.rankingLinkDescription') || 'Share this link to let others view the current league standings.' }}</p>
          <v-text-field
            :value="shareableLink"
            readonly
            outlined
            :label="$t('broker.leagues.shareableLink') || 'Shareable Link'"
            append-icon="fa-regular fa-copy"
            @click:append="copyToClipboard(shareableLink)">
          </v-text-field>
        </div>

        <!-- Social Media Sharing -->
        <div class="social-section">
          <h4 class="section-title mb-3">{{ $t('broker.leagues.shareOnSocialMedia') || 'Share on Social Media' }}</h4>
          <div class="d-flex justify-space-between align-center">
            <p class="social-subtitle mb-0">{{ $t('broker.leagues.shareViaSocial') || 'Share via social networks' }}</p>
            <div class="social-icons-container">
              <v-btn icon class="social-icon" style="background-color: transparent !important;" @click="shareToFacebook">
                <v-icon size="24" :color="primaryColor">fa-brands fa-facebook-f</v-icon>
              </v-btn>
              <v-btn icon class="social-icon" style="background-color: transparent !important;" @click="shareToTwitter">
                <v-icon size="24" :color="primaryColor">fa-brands fa-x-twitter</v-icon>
              </v-btn>
              <v-btn icon class="social-icon" style="background-color: transparent !important;" @click="shareToWhatsApp">
                <v-icon size="24" :color="primaryColor">fa-brands fa-whatsapp</v-icon>
              </v-btn>
              <v-btn icon class="social-icon" style="background-color: transparent !important;" @click="shareToTelegram">
                <v-icon size="24" :color="primaryColor">fa-brands fa-telegram</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'LeagueShare',
  props: {
    partnerId: {
      type: Number,
      required: true
    },
    tier: {
      type: String,
      required: true
    },
    partnerName: {
      type: String,
      default: ''
    },
    primaryColor: {
      type: String,
      default: '#28EA96'
    }
  },
  computed: {
    shareableLink() {
      const baseUrl = window.location.origin;
      // TODO: Update this with the actual public league view route when created
      return `${baseUrl}/leagues/public?partner=${this.partnerId}&tier=${this.tier}`;
    },
    shareMessage() {
      const tierName = this.tier.charAt(0).toUpperCase() + this.tier.slice(1);
      return this.$t('broker.leagues.shareMessage', {
        partnerName: this.partnerName,
        tier: tierName
      }) || `Check out the ${tierName} League rankings for ${this.partnerName}!`;
    }
  },
  methods: {
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$emit('copied');
      }).catch(err => {
        console.error('Could not copy text: ', err);
      });
    },
    shareToFacebook() {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.shareableLink)}`;
      window.open(url, '_blank', 'width=600,height=400');
    },
    shareToTwitter() {
      const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(this.shareableLink)}&text=${encodeURIComponent(this.shareMessage)}`;
      window.open(url, '_blank', 'width=600,height=400');
    },
    shareToWhatsApp() {
      const url = `https://wa.me/?text=${encodeURIComponent(this.shareMessage + ' ' + this.shareableLink)}`;
      window.open(url, '_blank');
    },
    shareToTelegram() {
      const url = `https://t.me/share/url?url=${encodeURIComponent(this.shareableLink)}&text=${encodeURIComponent(this.shareMessage)}`;
      window.open(url, '_blank');
    }
  }
}
</script>

<style lang="scss" scoped>
.league-share-dialog {
  max-height: 90vh;
  display: flex;
  flex-direction: column;

  .v-card__title {
    flex-shrink: 0;

    h2 {
      font-family: 'Avenir', sans-serif;
      font-size: 24px;
      font-weight: 600;
      color: var(--v-mainBlack-base, #1A1A1A);
      margin: 0;
    }
  }

  .v-card__text.scrollable-content {
    flex: 1;
    overflow-y: auto;
    max-height: calc(90vh - 120px);

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;

      &:hover {
        background: #a8a8a8;
      }
    }

    scrollbar-width: thin;
    scrollbar-color: #c1c1c1 #f1f1f1;
  }

  .link-section,
  .social-section {
    border: 1px solid #E0E0E0;
    border-radius: 12px;
    padding: 20px;
    background-color: rgba(0, 0, 0, 0.02);
  }

  .section-title {
    font-family: 'Avenir', sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: var(--v-mainBlack-base, #1A1A1A);
    margin: 0;
  }

  .section-description {
    font-family: 'Avenir', sans-serif;
    font-size: 14px;
    color: #666666;
    line-height: 1.4;
    margin: 0;
  }

  .social-subtitle {
    font-family: 'Avenir', sans-serif;
    font-size: 14px;
    color: var(--v-mainBlack-base, #1A1A1A);
    font-weight: 500;
  }

  .social-icons-container {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .social-icon {
    width: 36px;
    height: 36px;
    background-color: transparent !important;
    border: 1px solid currentColor !important;
    border-radius: 50% !important;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05) !important;
    }

    &::before {
      background-color: transparent !important;
    }

    .v-icon {
      font-size: 14px;
    }
  }
}

.icon {
  cursor: pointer;
  font-size: 20px;
  color: #666666;

  &:hover {
    color: #333333;
  }
}

// Responsive adjustments for mobile
@media (max-width: 600px) {
  .league-share-dialog {
    max-height: 95vh;
    margin: 8px;

    .v-card__text.scrollable-content {
      max-height: calc(95vh - 100px);
    }

    .link-section,
    .social-section {
      padding: 16px;
    }

    .social-icons-container {
      flex-wrap: wrap;
      gap: 6px;
    }
  }
}

::v-deep .v-icon.v-icon.v-icon--link {
  border: none;

  &:hover,
  &:focus,
  &:active {
    background-color: transparent !important;
    border: none !important;
    box-shadow: none !important;
    opacity: 0.8;
    color: var(--v-mainBlack-base, #333333) !important;

    &::before,
    &::after {
      border: none;
      background-color: transparent !important;
      color: var(--v-mainBlack-base, #333333) !important;
    }
  }
}

// Override broker app global button styles for social icons
::v-deep .social-icon.v-btn {
  background-color: transparent !important;

  &::before,
  &::after {
    background-color: transparent !important;
    opacity: 0 !important;
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.05) !important;

    &::before {
      opacity: 0 !important;
    }
  }

  &:focus,
  &:active {
    background-color: transparent !important;
  }
}
</style>
